import { createContext } from 'preact';
import { useContext, useState, useRef, useCallback, useEffect } from 'preact/hooks';
import { OddballParser } from '../src/parser';
import { GestureDetector } from '../src/gesture-detector';
import { RollDetector } from '../src/roll-detector';
import { ThrowDetector } from '../src/throw-detector';
import { WebMidiAdapter } from '../src/adapters/web-midi';
import { WebBluetoothAdapter } from '../src/adapters/web-bluetooth';
import { loadInstrument, isInstrumentLoaded, setCurrentProgram } from './audio/soundfont-player';

type ConnectionMethod = 'webmidi' | 'webbluetooth';
export type SynthMode = 'oscillator' | 'soundfont';

interface OddballContextValue {
  parser: OddballParser;
  detector: GestureDetector;
  rollDetector: RollDetector;
  throwDetector: ThrowDetector;
  connected: boolean;
  status: string;
  connect: () => Promise<void>;
  disconnect: () => void;
  connectionMethod: ConnectionMethod;
  setConnectionMethod: (method: ConnectionMethod) => void;
  bleAvailable: boolean;
  /** Speed multiplier (0.25–4.0, default 1.0). Tools divide intervals by this. */
  speedModifier: number;
  setSpeedModifier: (v: number) => void;
  /** Octave offset (-2 to +2, default 0). Tools multiply freq by 2^offset. */
  octaveOffset: number;
  setOctaveOffset: (v: number) => void;
  /** Whether to auto-disconnect after 2 minutes of inactivity. */
  autoIdleDisconnect: boolean;
  setAutoIdleDisconnect: (v: boolean) => void;
  /** Audio synthesis mode: oscillator (default) or soundfont samples. */
  synthMode: SynthMode;
  setSynthMode: (v: SynthMode) => void;
  /** GM program number for soundfont mode (0-indexed). */
  gmProgram: number;
  setGmProgram: (v: number) => void;
  /** True while a soundfont instrument is being fetched. */
  instrumentLoading: boolean;
}

const bleAvailable = typeof navigator !== 'undefined' && !!navigator.bluetooth;

const OddballContext = createContext<OddballContextValue | null>(null);

export function OddballProvider({ children }: { children: preact.ComponentChildren }) {
  const parserRef = useRef(new OddballParser());
  const detectorRef = useRef(new GestureDetector());
  const adapterRef = useRef<WebMidiAdapter | WebBluetoothAdapter | null>(null);
  const idleIntervalRef = useRef<number>(0);
  const lastActivityRef = useRef<number>(0);

  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState('Click Connect to start');
  const [connectionMethod, setConnectionMethod] = useState<ConnectionMethod>(bleAvailable ? 'webbluetooth' : 'webmidi');
  const [speedModifier, setSpeedModifier] = useState(1.0);
  const [octaveOffset, setOctaveOffset] = useState(0);
  const [autoIdleDisconnect, setAutoIdleDisconnect] = useState(true);
  const [synthMode, setSynthMode] = useState<SynthMode>('soundfont');
  const [gmProgram, setGmProgram] = useState(0);
  const [instrumentLoading, setInstrumentLoading] = useState(false);
  const autoIdleRef = useRef(true);

  // Keep ref in sync so the interval callback sees the latest value
  autoIdleRef.current = autoIdleDisconnect;

  // Keep soundfont program in sync and auto-load when switching to soundfont mode
  useEffect(() => {
    setCurrentProgram(gmProgram);
    if (synthMode === 'soundfont' && !isInstrumentLoaded(gmProgram)) {
      setInstrumentLoading(true);
      loadInstrument(gmProgram).then(() => setInstrumentLoading(false));
    }
  }, [synthMode, gmProgram]);

  // Wire parser → detector + activity tracking
  const wiredRef = useRef(false);
  if (!wiredRef.current) {
    wiredRef.current = true;
    parserRef.current.on('event', (evt) => {
      // Only MIDI notes (tap/shake/twist) count as activity for auto-disconnect;
      // CC events stream continuously even when the ball is idle.
      if (evt.type === 'tap' || evt.type === 'shake' || evt.type === 'twist') {
        lastActivityRef.current = performance.now();
      }
      detectorRef.current.feed(evt);
    });
    // Rolling above 0.5 Hz also counts as activity
    detectorRef.current.roll.on('rolling', (evt) => {
      if (evt.oscillationFreq > 0.5) {
        lastActivityRef.current = performance.now();
      }
    });
  }

  const connect = useCallback(async () => {
    const parser = parserRef.current;

    // Disconnect existing adapter if switching methods
    if (adapterRef.current) {
      adapterRef.current.disconnect();
      adapterRef.current = null;
    }

    const onStatusChange = (msg: string, state: 'connected' | 'disconnected' | 'error') => {
      setStatus(msg);
      setConnected(state === 'connected');
    };

    if (connectionMethod === 'webbluetooth') {
      adapterRef.current = new WebBluetoothAdapter(parser, { onStatusChange });
    } else {
      adapterRef.current = new WebMidiAdapter(parser, { onStatusChange });
    }

    await adapterRef.current.connect();

    lastActivityRef.current = performance.now();

    // Start idle check interval (also auto-disconnects after 2 min inactivity)
    if (!idleIntervalRef.current) {
      idleIntervalRef.current = window.setInterval(() => {
        const now = performance.now();
        detectorRef.current.checkIdle(now);
        if (autoIdleRef.current && lastActivityRef.current > 0 && now - lastActivityRef.current >= 120_000) {
          adapterRef.current?.disconnect();
          adapterRef.current = null;
          setConnected(false);
          setStatus('Disconnected (inactive)');
          clearInterval(idleIntervalRef.current);
          idleIntervalRef.current = 0;
        }
      }, 500);
    }
  }, [connectionMethod]);

  const disconnect = useCallback(() => {
    adapterRef.current?.disconnect();
    adapterRef.current = null;
    setConnected(false);
    setStatus('Disconnected');
    if (idleIntervalRef.current) {
      clearInterval(idleIntervalRef.current);
      idleIntervalRef.current = 0;
    }
  }, []);

  return (
    <OddballContext.Provider
      value={{
        parser: parserRef.current,
        detector: detectorRef.current,
        rollDetector: detectorRef.current.roll,
        throwDetector: detectorRef.current.throw,
        connected,
        status,
        connect,
        disconnect,
        connectionMethod,
        setConnectionMethod,
        bleAvailable,
        speedModifier,
        setSpeedModifier,
        octaveOffset,
        setOctaveOffset,
        autoIdleDisconnect,
        setAutoIdleDisconnect,
        synthMode,
        setSynthMode,
        gmProgram,
        setGmProgram,
        instrumentLoading,
      }}
    >
      {children}
    </OddballContext.Provider>
  );
}

export function useOddball(): OddballContextValue {
  const ctx = useContext(OddballContext);
  if (!ctx) throw new Error('useOddball must be used within OddballProvider');
  return ctx;
}
